import { PaletteOptions, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    xl: React.CSSProperties;
    l: React.CSSProperties;
    m: React.CSSProperties;
    r: React.CSSProperties;
    s: React.CSSProperties;
    xs: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    xl?: React.CSSProperties;
    l: React.CSSProperties;
    m: React.CSSProperties;
    r: React.CSSProperties;
    s: React.CSSProperties;
    xs: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    xl: true;
    l: true;
    m: true;
    r: true;
    s: true;
    xs: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    brand: true;
    accents: true;
  }
}

interface CommonThemePaletteOptions extends PaletteOptions {
  brand?: {
    primary: string,
    hover: string,
    pressed: string,
    snow: string,
    charcoal: string
  },
  accents?: {
    blue: string,
    green: string,
    red: string,
    yellow: string
  },
  text: {
    primary: string,
    secondary: string
    disabled: string
    tertiary: string
  },
  background: {
    default: string
    paper: string
    secondary: string
    border: string
    primary: string
    translucent: string
  }
}
export interface CommonThemeOptions extends ThemeOptions {
  palette: CommonThemePaletteOptions
}

export const customPaletteColors = {
  success: {
    main: '#97E3C2'
  },
  error: {
    main: '#FF3B30'
  },
  warning: {
    main: '#FFFEAD'
  },
  contrastDarkText: '#FFFFFF',
  contrastLightText: '#000000',
  brand: {
    primary: '#23FFB0',
    pressed: '#1CCC8D',
    hover: '#15996A',
    snow: '#FFFFFF',
    charcoal: '#000000'
  },
  accents: {
    blue: '#A4D7EC',
    green: '#97E3C2',
    red: '#FF3B30',
    yellow: '#FF9500'
  },
  dark: {
    text: {
      primary: '#F8FFFE',
      secondary: '#B8C3C3',
      tertiary: '#718888',
      disabled: '#2E4040',
    },
    background: {
      primary: '#101918',
      secondary: '#1E2625',
      tertiary: '#243433',
      border: 'rgba(177, 255, 227, 0.2)',
      translucent: 'rgba(255, 255, 255, 0.12)'
    },
  },
  light: {
    text: {
      primary: '#101818',
      secondary: '#3E5555',
      tertiary: '#89A0A0',
      disabled: '#B8C3C3',
    },
    background: {
      primary: '#F0FFF9',
      secondary: '#D3FFEF',
      tertiary: '#A7FFDF',
      border: '#b1ffe333',
      translucent: 'rgba(255, 255, 255, 0.12)'
    },
  }
}

export const healthBarColors = {
  healthy: customPaletteColors.brand.primary,
  risky: customPaletteColors.accents.yellow,
  danger: customPaletteColors.accents.red
}

const constructTheme = (coloredTheme: 'dark' | 'light') => {
  const commonThemeOptions: CommonThemeOptions = {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1170,
        xl: 1400,
      },
    },
    shape: {
      borderRadius: 8
    },
    palette: {
      ...customPaletteColors,
      primary: {
        main: customPaletteColors.brand.primary, // main showing color
        light: customPaletteColors.brand.pressed, //lighter
        dark: customPaletteColors.brand.hover, //hover
        contrastText: customPaletteColors.contrastLightText
      },
      secondary: {
        main: customPaletteColors.brand.primary, // main showing color
        light: customPaletteColors.brand.pressed, //lighter
        dark: customPaletteColors.brand.hover, //hover
        contrastText: customPaletteColors.contrastDarkText
      },
      background: {
        default: customPaletteColors[coloredTheme].background.primary,
        paper: customPaletteColors[coloredTheme].background.primary,
        primary: customPaletteColors[coloredTheme].background.primary,
        secondary: customPaletteColors[coloredTheme].background.secondary,
        border: customPaletteColors[coloredTheme].background.border,
        translucent: customPaletteColors[coloredTheme].background.translucent
      },
      text: {
        primary: customPaletteColors[coloredTheme].text.primary,
        secondary: customPaletteColors[coloredTheme].text.secondary,
        tertiary: customPaletteColors[coloredTheme].text.tertiary,
        disabled: customPaletteColors[coloredTheme].text.disabled
      },
    },
    typography: {
      allVariants: {
        color: customPaletteColors[coloredTheme].text.primary,
        textTransform: 'none',
      },
      h1: {
        fontSize: '3.75rem',
        fontFamily: 'Space Grotesk',
      },
      h2: {
        fontSize: '3rem',
        fontFamily: 'Space Grotesk',
      },
      h3: {
        fontSize: '2rem',
        fontFamily: 'Space Grotesk',
      },
      h4: {
        fontSize: '1.75rem',
        fontFamily: 'Space Grotesk',
      },
      h5: {
        fontSize: '1.5rem',
        fontFamily: 'Space Grotesk',
      },
      h6: {
        fontSize: '1.25rem',
        fontFamily: 'Space Grotesk',
      },
      xl: {
        fontSize: '1.375rem',
        fontFamily: 'Inter',
      },
      l: {
        fontSize: '1.25rem',
        fontFamily: 'Inter',
      },
      m: {
        fontSize: '1.125rem',
        fontFamily: 'Inter',
      },
      r: {
        fontSize: '1rem',
        fontFamily: 'Inter',
      },
      s: {
        fontSize: '0.875rem',
        fontFamily: 'Inter',
      },
      xs: {
        fontSize: '0.75rem',
        fontFamily: 'Inter',
      },

    },
    components: {
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: customPaletteColors[coloredTheme].text.primary
          }
        }
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: customPaletteColors[coloredTheme].text.primary
          }
        }
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true
        },
        styleOverrides: {
          root: () => ({
            ":disabled": {
              color: customPaletteColors[coloredTheme].text.disabled
            }
          }),
        }
      },
      MuiButton: {
        defaultProps: {
          disableRipple: true
        }
      },
    }
  }
  return commonThemeOptions
}

export default constructTheme